import { styled } from '@linaria/react';
import type { AgentMinerSessionLinkedTask } from '../../../api/agent-miner-client/agent-miner-client.type';
import { secondaryBase, secondaryLight, secondaryLightest } from '../../../styles/design-tokens';
import { Tooltip } from '../../../ui-library/tooltip/tooltip';
import { normalizeLabelPart } from './shared.helpers';
import { ChipSize, fontBySize, paddingBySize } from './task-chip.type';

type TaskChipProps = {
  task: AgentMinerSessionLinkedTask;
  size?: ChipSize;
  showFullLabel?: boolean;
  onClick?: (task: AgentMinerSessionLinkedTask) => void;
};

export function TaskChip({ task, size = 'md', showFullLabel = false, onClick }: TaskChipProps) {
  const code = normalizeLabelPart(task.task_code);
  const name = normalizeLabelPart(task.task_name);
  const fullLabel = code && name ? `${code}: ${name}` : (code ?? name ?? 'Task');
  const label = showFullLabel ? fullLabel : (code ?? name ?? 'Task');
  const tooltipLabel = showFullLabel ? (name ?? code ?? '') : (name ?? code ?? '');
  const clickable = Boolean(onClick);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!onClick) return;
    event.stopPropagation();
    onClick(task);
  };

  const chip = (
    <ChipButton type="button" chipSize={size} clickable={clickable} onClick={handleClick} disabled={!clickable}>
      {label}
    </ChipButton>
  );

  if (!tooltipLabel) return chip;

  return (
    <Tooltip label={tooltipLabel} variant="black" multiline style={{ maxWidth: 260 }}>
      {chip}
    </Tooltip>
  );
}

const ChipButton = styled.button<{ chipSize: ChipSize; clickable: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: ${(props) => paddingBySize[props.chipSize]};
  border: 1px solid ${secondaryLight};
  background: ${secondaryLightest};
  color: ${secondaryBase};
  font-family: 'Figtree', sans-serif;
  font-weight: 500;
  font-size: ${(props) => fontBySize[props.chipSize]};
  line-height: 1.2;
  border-radius: 16px;
  white-space: nowrap;
  cursor: ${(props) => (props.clickable ? 'pointer' : 'default')};
  transition: background 0.12s ease, border-color 0.12s ease;
  appearance: none;

  &:hover {
    background: ${(props) => (props.clickable ? secondaryLight : secondaryLightest)};
    color: ${(props) => (props.clickable ? '#fff' : secondaryBase)};
  }

  &:disabled {
    opacity: 1;
    color: ${secondaryBase};
    background: ${secondaryLightest};
    border-color: ${secondaryLight};
  }
`;
